import { ImportButton } from "@/components/ImportButton";

import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import api, { apiErr, downloadXlsx } from "@/lib/api";
import { fmtEuro, fmtNum, fmtDate, RUN_TYPE_LABEL, RUN_STATUS_LABEL } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { ArrowLeft, FileText, Printer, Save, Lock, Unlock, FileSpreadsheet, Timer, FileDown, Trash2, RefreshCw, RotateCcw } from "lucide-react";
import { exportElementToPdf } from "@/lib/pdf";
import PayslipModal from "@/components/PayslipModal";
import BulkPayslips from "@/components/BulkPayslips";

const TYPE_TINT = {
  monthly: "bg-[#EEF0EA] text-[#6E7A63]",
  christmas: "bg-[#FEF2F2] text-[#991B1B]",
  easter: "bg-[#FFFBEB] text-[#92400E]",
  leave_compensation: "bg-[#45423C]/10 text-[#45423C]",
  leave_allowance: "bg-[#B9AE9A]/20 text-[#6F6A60]",
};

function RunList({ onOpen }) {
  const { data = [], isLoading } = useQuery({
    queryKey: ["runs"],
    queryFn: async () => (await api.get("/payroll/runs")).data,
  });
  const [year, setYear] = useState(null);
  if (isLoading) return <div className="text-[#6F6A60] py-10 text-center">Φόρτωση μισθοδοσιών…</div>;
  const years = Array.from(new Set(data.map((r) => r.year))).sort((a, b) => b - a);
  const activeYear = year ?? years[0];
  const rows = data.filter((r) => r.year === activeYear);
  const total = rows.reduce((s, r) => s + (r.total_gross || 0), 0);
  const sum = (k) => Math.round(rows.reduce((s, r) => s + (Number(r[k]) || 0), 0) * 100) / 100;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2" data-testid="runs-year-filter">
        <span className="text-[10px] uppercase tracking-wider text-[#6F6A60] font-semibold mr-1">Έτος</span>
        {years.map((y) => (
          <button key={y} data-testid={`runs-year-${y}`} onClick={() => setYear(y)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              y === activeYear ? "bg-[#45423C] text-white border-[#45423C]" : "bg-white text-[#6F6A60] border-[#E3DED4] hover:border-[#B9AE9A]"}`}>
            {y}
          </button>
        ))}
      </div>
    <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="bg-[#F1EDE6] text-left text-[10px] uppercase tracking-wider text-[#6F6A60]">
              <th className="px-4 py-2.5 font-semibold">Μισθοδοσία</th>
              <th className="px-3 py-2.5 font-semibold">Τύπος</th>
              <th className="px-3 py-2.5 font-semibold">Λήξη</th>
              <th className="px-3 py-2.5 font-semibold text-center">Άτομα</th>
              <th className="px-3 py-2.5 font-semibold text-right">% εργασίας</th>
              <th className="px-3 py-2.5 font-semibold text-right">Υπερωρίες €</th>
              <th className="px-3 py-2.5 font-semibold text-right">Ώρες Υπ.</th>
              <th className="px-3 py-2.5 font-semibold text-right">Αργίες €</th>
              <th className="px-3 py-2.5 font-semibold text-right">Μηνιαίες</th>
              <th className="px-3 py-2.5 font-semibold text-right">Συνολικές Αποδοχές</th>
              <th className="px-3 py-2.5 font-semibold text-right">ΦΜΥ</th>
              <th className="px-3 py-2.5 font-semibold text-right">ΕΦΚΑ</th>
              <th className="px-3 py-2.5 font-semibold text-right bg-[#2E6F7A]/15 text-[#1f5560]">Συνολικό Κόστος</th>
              <th className="px-3 py-2.5 font-semibold text-right bg-[#6E7A63]/15 text-[#4A5A3E]">Τράπεζα</th>
              <th className="px-3 py-2.5 font-semibold text-right bg-[#6E7A63]/15 text-[#4A5A3E]">Μετρητά</th>
              <th className="px-3 py-2.5 font-semibold text-right">Κατάσταση</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                data-testid={`run-card-${r.source_file || r.id}`}
                onClick={() => onOpen(r.id)}
                className="border-t border-[#E3DED4] hover:bg-[#F7F5F1] cursor-pointer"
              >
                <td className="px-4 py-2 font-medium text-[#45423C]">{r.label_el || r.label_en}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${TYPE_TINT[r.run_type] || "bg-[#F1EDE6] text-[#6F6A60]"}`}>
                    {RUN_TYPE_LABEL[r.run_type] || r.run_type}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-[#6F6A60] tabular">{fmtDate(r.period_end)}</td>
                <td className="px-3 py-2 text-center tabular">{r.employee_count}</td>
                <td className="px-3 py-2 text-right tabular">{r.total_pct || 0}%</td>
                <td className="px-3 py-2 text-right tabular">{fmtEuro(r.total_overtime)}</td>
                <td className="px-3 py-2 text-right tabular text-[#6F6A60]">{r.total_ot_hours || 0}</td>
                <td className="px-3 py-2 text-right tabular">{fmtEuro(r.total_holiday)}</td>
                <td className="px-3 py-2 text-right tabular">{fmtEuro(r.total_base)}</td>
                <td className="px-3 py-2 text-right tabular font-semibold">{fmtEuro(r.total_gross)}</td>
                <td className="px-3 py-2 text-right tabular">{fmtEuro(r.total_tax)}</td>
                <td className="px-3 py-2 text-right tabular">{fmtEuro(r.total_efka)}</td>
                <td className="px-3 py-2 text-right tabular font-semibold text-[#1f5560] bg-[#2E6F7A]/5" data-testid={`run-cost-${r.id}`}>{fmtEuro(r.total_cost)}</td>
                <td className="px-3 py-2 text-right tabular bg-[#6E7A63]/5">{fmtEuro(r.total_bank)}</td>
                <td className="px-3 py-2 text-right tabular text-[#4A5A3E] font-medium bg-[#6E7A63]/5">{fmtEuro(r.total_cash)}</td>
                <td className={`px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider ${r.status === "finalized" ? "text-[#6E7A63]" : "text-[#92400E]"}`}>
                  {RUN_STATUS_LABEL[r.status] || r.status}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[#45423C] bg-[#F1EDE6] font-semibold text-[#45423C]">
              <td className="px-4 py-2.5" colSpan={4}>ΣΥΝΟΛΟ {rows.length} μισθοδοσιών {activeYear}</td>
              <td className="px-3 py-2.5 text-right tabular" />
              <td className="px-3 py-2.5 text-right tabular">{fmtEuro(sum("total_overtime"))}</td>
              <td className="px-3 py-2.5 text-right tabular">{sum("total_ot_hours")}</td>
              <td className="px-3 py-2.5 text-right tabular">{fmtEuro(sum("total_holiday"))}</td>
              <td className="px-3 py-2.5 text-right tabular">{fmtEuro(sum("total_base"))}</td>
              <td className="px-3 py-2.5 text-right tabular" data-testid="runs-total">{fmtEuro(total)}</td>
              <td className="px-3 py-2.5 text-right tabular">{fmtEuro(sum("total_tax"))}</td>
              <td className="px-3 py-2.5 text-right tabular">{fmtEuro(sum("total_efka"))}</td>
              <td className="px-3 py-2.5 text-right tabular text-[#1f5560] bg-[#2E6F7A]/10" data-testid="runs-total-cost">{fmtEuro(sum("total_cost"))}</td>
              <td className="px-3 py-2.5 text-right tabular bg-[#6E7A63]/10">{fmtEuro(sum("total_bank"))}</td>
              <td className="px-3 py-2.5 text-right tabular bg-[#6E7A63]/10">{fmtEuro(sum("total_cash"))}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
    </div>
  );
}

function RunDetail({ runId, onBack }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["owner", "hr"].includes(user?.role);
  const [items, setItems] = useState(null);
  const [payslip, setPayslip] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  const [dept, setDept] = useState("all");
  const [otPreview, setOtPreview] = useState(null);
  const { data: run, isLoading } = useQuery({
    queryKey: ["run", runId],
    queryFn: async () => {
      const d = (await api.get(`/payroll/runs/${runId}`)).data;
      setItems(d.line_items);
      return d;
    },
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => (await api.get("/employees")).data,
  });

  const finalized = run?.status === "finalized";
  const editable = canEdit && !finalized;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["runs"] });
    qc.invalidateQueries({ queryKey: ["run", runId] });
    qc.invalidateQueries({ queryKey: ["budget-compare"] });
  };

  const save = useMutation({
    mutationFn: async () => (await api.put(`/payroll/runs/${runId}`, { ...run, line_items: items })).data,
    onSuccess: (d) => { setItems(d.line_items); invalidate(); toast.success("Η μισθοδοσία αποθηκεύτηκε & υπολογίστηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const finalize = useMutation({
    mutationFn: async () => (await api.put(`/payroll/runs/${runId}/finalize`)).data,
    onSuccess: () => { invalidate(); toast.success("Η μισθοδοσία οριστικοποιήθηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const removeRun = useMutation({
    mutationFn: async (permanent) => (await api.delete(`/payroll/runs/${runId}${permanent ? "?permanent=true" : ""}`)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["runs"] }); qc.invalidateQueries({ queryKey: ["trash"] });
      toast.success(d.status === "erased" ? "Η μισθοδοσία διαγράφηκε οριστικά" : "Η μισθοδοσία μεταφέρθηκε στον Κάδο");
      onBack();
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const askRemoveRun = () => {
    if (!window.confirm(`Διαγραφή της μισθοδοσίας «${run?.label_el || run?.name || ""}»;`)) return;
    removeRun.mutate(window.confirm("ΟΡΙΣΤΙΚΗ διαγραφή;\n\nOK = σβήνεται εντελώς (κρατείται μόνο backup) · Άκυρο = πάει στον Κάδο Ανακύκλωσης"));
  };
  const unfinalize = useMutation({
    mutationFn: async () => (await api.put(`/payroll/runs/${runId}/unfinalize`)).data,
    onSuccess: () => { invalidate(); toast.success("Η οριστικοποίηση αναιρέθηκε — διόρθωση επιτρέπεται"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const previewOt = useMutation({
    mutationFn: async () => (await api.get(`/payroll/runs/${runId}/overtime-preview`)).data,
    onSuccess: (d) => {
      setOtPreview(d);
      if (!d.rows.length) toast.info("Δεν βρέθηκαν υπερωρίες από το ωρολόγιο για αυτόν τον μήνα");
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const applyOt = useMutation({
    mutationFn: async () => (await api.post(`/payroll/runs/${runId}/apply-overtime`)).data,
    onSuccess: (d) => { setOtPreview(null); invalidate(); toast.success(`Ενημερώθηκαν ${d.updated} υπάλληλοι από το ωρολόγιο`); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const syncRates = useMutation({
    mutationFn: async (overwrite) => (await api.post(`/payroll/runs/${runId}/sync-rates?overwrite=${overwrite ? "true" : "false"}`)).data,
    onSuccess: (d) => {
      invalidate();
      toast.success(d.updated
        ? `Ενημερώθηκαν ${d.updated} τιμές υπερωρίας από τις καρτέλες${d.missing_rate.length ? ` · ${d.missing_rate.length} χωρίς τιμή στην καρτέλα` : ""}`
        : "Όλες οι τιμές υπερωρίας είναι ήδη ενημερωμένες");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  useEffect(() => {
    if (showBulk === "print") {
      const t = setTimeout(() => { window.print(); setShowBulk(false); }, 300);
      return () => clearTimeout(t);
    }
    if (showBulk === "pdf") {
      const t = setTimeout(async () => {
        const el = document.getElementById("bulk-payslips");
        if (el) await exportElementToPdf(el, `TAF_Apodeixeis_${run?.month || ""}_${run?.year || ""}.pdf`);
        setShowBulk(false);
      }, 400);
      return () => clearTimeout(t);
    }
  }, [showBulk]);

  if (isLoading || !run || !items) return <div className="text-[#6F6A60] py-10 text-center">Φόρτωση…</div>;

  const upd = (i, key, val) => {
    const next = [...items];
    next[i] = { ...next[i], [key]: key === "name" || key === "comments" ? val : Number(val) };
    const rate = Number(next[i].overtime_rate) || 0;
    const hours = Number(next[i].overtime_hours) || 0;
    if ((key === "overtime_rate" || key === "overtime_hours") && rate > 0) {
      next[i].overtime_amount = Math.round(rate * hours * 100) / 100;
    }
    const base = (Number(next[i].monthly_base) || 0) * (Number(next[i].work_pct) || 0);
    next[i].total_earnings = Math.round((base + (Number(next[i].overtime_amount) || 0) + (Number(next[i].holiday_amount) || 0)) * 100) / 100;
    next[i].remaining = Math.round((next[i].total_earnings - (Number(next[i].bank_deposit) || 0)) * 100) / 100;
    setItems(next);
  };

  const active = (it) => (it.total_earnings || 0) > 0 || (it.work_pct || 0) > 0;

  const depts = Array.from(new Set(items.map((i) => i.department).filter(Boolean))).sort();
  const visible = items.map((it, i) => ({ it, i })).filter(({ it }) => dept === "all" || it.department === dept);
  const working = visible.filter(({ it }) => active(it));
  const idle = visible.filter(({ it }) => !active(it));
  const totalGross = working.reduce((s, { it }) => s + (it.total_earnings || 0), 0);
  const totalBank = working.reduce((s, { it }) => s + (it.bank_deposit || 0), 0);
  const totalTax = working.reduce((s, { it }) => s + (it.tax || 0), 0);
  const totalEfka = working.reduce((s, { it }) => s + (Number(it.efka) || 0), 0);
  const totalHoliday = working.reduce((s, { it }) => s + (Number(it.holiday_amount) || 0), 0);
  const totalBase = working.reduce((s, { it }) => s + (it.monthly_base || 0), 0);
  const totalOt = working.reduce((s, { it }) => s + (it.overtime_amount || 0), 0);
  const totalOtHours = working.reduce((s, { it }) => s + (it.overtime_hours || 0), 0);
  const totalPct = working.reduce((s, { it }) => s + (it.work_pct || 0) * 100, 0);
  const inRun = new Set(items.map((it) => it.name));
  const available = (employees || []).filter((e) => !inRun.has(e.name));

  const addEmployee = (name) => {
    const e = (employees || []).find((x) => x.name === name);
    if (!e) return;
    setItems([...items, {
      code: e.code, name: e.name, department: e.department || "", position: e.position || e.department || "",
      work_pct: 1, monthly_base: e.monthly_base || 0, overtime_amount: 0, overtime_hours: 0,
      overtime_rate: e.overtime_rate || 0,
      bank_deposit: 0, total_earnings: e.monthly_base || 0, remaining: e.monthly_base || 0,
    }]);
    toast.success(`Προστέθηκε ${e.name} — πατήστε «Αποθήκευση»`);
  };
  const removeItem = (i) => {
    setItems(items.filter((_, j) => j !== i));
    toast.success("Αφαιρέθηκε — πατήστε «Αποθήκευση»");
  };
  const restoreItem = (i) => {
    const it = items[i];
    const e = (employees || []).find((x) => x.name === it.name || x.code === it.code);
    const base = Number(e?.monthly_base ?? it.monthly_base) || 0;
    const rate = Number(e?.overtime_rate ?? it.overtime_rate) || 0;
    const next = [...items];
    next[i] = {
      ...it,
      work_pct: 1,
      monthly_base: base,
      overtime_rate: rate,
      total_earnings: Math.round((base + (Number(it.overtime_amount) || 0)) * 100) / 100,
      remaining: Math.round((base + (Number(it.overtime_amount) || 0) - (Number(it.bank_deposit) || 0)) * 100) / 100,
    };
    setItems(next);
    toast.success(`Επαναφορά ${it.name} — 100% & μισθός ${fmtEuro(base)} από την καρτέλα. Πατήστε «Αποθήκευση».`);
  };
  const ci = "w-full bg-transparent text-right tabular outline-none focus:bg-[#FFFBEB] rounded px-1 py-0.5";
  const selOnFocus = (e) => e.target.select();

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <button data-testid="run-back" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-[#6F6A60] hover:text-[#45423C]">
          <ArrowLeft className="h-4 w-4" /> Όλες οι μισθοδοσίες
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="payroll-dept-filter" value={dept} onChange={(e) => setDept(e.target.value)}
            className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#45423C]">
            <option value="all">Όλα τα τμήματα</option>
            {depts.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          {editable && run.run_type === "monthly" && (
            <button data-testid="import-overtime-btn" onClick={() => previewOt.mutate()} disabled={previewOt.isPending}
              className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold hover:border-[#C86235] text-[#C86235] disabled:opacity-50">
              <Timer className="h-4 w-4" /> {previewOt.isPending ? "Υπολογισμός…" : "Εισαγωγή υπερωριών"}
            </button>
          )}
          {editable && (
            <button data-testid="sync-rates-btn" onClick={() => syncRates.mutate(true)} disabled={syncRates.isPending}
              title="Φέρνει την τιμή υπερωρίας κάθε υπαλλήλου από την καρτέλα του"
              className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold hover:border-[#45423C] text-[#45423C] disabled:opacity-50">
              <RefreshCw className="h-4 w-4" /> {syncRates.isPending ? "Ενημέρωση…" : "Τιμές υπερωρίας από καρτέλες"}
            </button>
          )}
          <button data-testid="payroll-export-xlsx" onClick={() => downloadXlsx(`/export/payroll.xlsx?run_id=${runId}`, `TAF_Misthodosia_${run.month || ""}_${run.year || ""}.xlsx`)}
            className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold hover:border-[#6E7A63] text-[#6E7A63]">
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </button>
          <button data-testid="pdf-all-btn" onClick={() => setShowBulk("pdf")} className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold hover:border-[#B9AE9A]">
            <FileDown className="h-4 w-4" /> {showBulk === "pdf" ? "Δημιουργία…" : "PDF όλων"}
          </button>
          <button data-testid="print-all-btn" onClick={() => setShowBulk("print")} className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold hover:border-[#B9AE9A]">
            <Printer className="h-4 w-4" /> Εκτύπωση όλων
          </button>
          {canEdit && !finalized && (
            <button data-testid="run-save" onClick={() => save.mutate()} disabled={save.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-[#6E7A63] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              <Save className="h-4 w-4" /> {save.isPending ? "Αποθήκευση…" : "Αποθήκευση"}
            </button>
          )}
          {canEdit && !finalized && (
            <button data-testid="finalize-btn" onClick={() => finalize.mutate()} disabled={finalize.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
              <Lock className="h-4 w-4" /> Οριστικοποίηση
            </button>
          )}
          {canEdit && finalized && (
            <button data-testid="unfinalize-btn" onClick={() => unfinalize.mutate()} disabled={unfinalize.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-[#C86235] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              <Unlock className="h-4 w-4" /> Αναίρεση Οριστικοποίησης
            </button>
          )}
          {user?.role === "owner" && (
            <button data-testid="delete-run-btn" onClick={askRemoveRun} disabled={removeRun.isPending} title="Διαγραφή μισθοδοσίας"
              className="inline-flex items-center gap-2 rounded-xl border border-[#991B1B] text-[#991B1B] px-3 py-2.5 text-sm font-semibold hover:bg-[#FEF2F2] disabled:opacity-50">
              <Trash2 className="h-4 w-4" /> Διαγραφή
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="font-serif text-3xl text-[#45423C]">{run.label_el || run.label_en}</h1>
        <span className="text-sm text-[#6F6A60]">· {fmtDate(run.period_end)}</span>
        <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${finalized ? "bg-[#ECFDF5] text-[#065F46]" : "bg-[#FFFBEB] text-[#92400E]"}`}>
          {RUN_STATUS_LABEL[run.status]}
        </span>
      </div>

      {otPreview && (
        <div className="rounded-2xl border border-[#C86235] bg-[#FFFBEB] overflow-hidden no-print" data-testid="overtime-preview">
          <div className="px-5 py-4 border-b border-[#C86235]/30 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="font-medium text-[#45423C]">Προεπισκόπηση εγκεκριμένων υπερωριών</h3>
              <p className="text-xs text-[#6F6A60] mt-0.5">Μόνο υπερωρίες <b>εγκεκριμένες από υπεύθυνο</b> (Υπερωρίες &amp; Έγκριση) × τιμή υπερωρίας του υπαλλήλου. Δεν γράφεται τίποτα πριν πατήσετε «Εφαρμογή».</p>
              {otPreview.pending_approvals > 0 && (
                <p className="text-xs font-semibold text-[#92400E] mt-1" data-testid="overtime-pending-warning">
                  Προσοχή: {otPreview.pending_approvals} αίτημα/αιτήματα υπερωριών του μήνα είναι ακόμη σε εκκρεμότητα και δεν περιλαμβάνονται.
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button data-testid="overtime-apply" onClick={() => applyOt.mutate()} disabled={applyOt.isPending || !otPreview.rows.length}
                className="rounded-xl bg-[#C86235] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                {applyOt.isPending ? "Εφαρμογή…" : "Εφαρμογή στη μισθοδοσία"}
              </button>
              <button data-testid="overtime-cancel" onClick={() => setOtPreview(null)} className="text-sm text-[#6F6A60] hover:text-[#45423C] px-2">Άκυρο</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/60 text-left text-[10px] uppercase tracking-wider text-[#6F6A60]">
                  <th className="px-4 py-2">Υπάλληλος</th>
                  <th className="px-4 py-2 text-right">Προγραμμ. ώρες</th>
                  <th className="px-4 py-2 text-right">Πραγματικές</th>
                  <th className="px-4 py-2 text-right">Υπερωρίες (ώ.)</th>
                  <th className="px-4 py-2 text-right">€/ώρα</th>
                  <th className="px-4 py-2 text-right">Ποσό</th>
                  <th className="px-4 py-2 text-right">Αργίες (ώ.)</th>
                  <th className="px-4 py-2 text-right">Αργίες € (+75%)</th>
                </tr>
              </thead>
              <tbody>
                {otPreview.rows.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-6 text-center text-[#6F6A60]">Καμία υπερωρία ή αργία για τον μήνα.</td></tr>
                )}
                {otPreview.rows.map((r) => (
                  <tr key={r.employee_name} data-testid={`ot-row-${r.employee_name}`} className="border-t border-[#C86235]/20">
                    <td className="px-4 py-2 font-medium text-[#45423C]">
                      {r.employee_name}
                      {!r.in_payroll && <span className="ml-2 text-[10px] text-[#991B1B]">δεν είναι στη μισθοδοσία</span>}
                      {r.missing_rate && <span className="ml-2 text-[10px] text-[#991B1B]">χωρίς τιμή υπερωρίας</span>}
                    </td>
                    <td className="px-4 py-2 text-right tabular">{fmtNum(r.scheduled_hours)}</td>
                    <td className="px-4 py-2 text-right tabular">{fmtNum(r.actual_hours)}</td>
                    <td className="px-4 py-2 text-right tabular font-semibold text-[#C86235]">{fmtNum(r.overtime_hours)}</td>
                    <td className="px-4 py-2 text-right tabular">{r.overtime_rate ? fmtEuro(r.overtime_rate) : "—"}</td>
                    <td className="px-4 py-2 text-right tabular font-semibold">{fmtEuro(r.overtime_amount)}</td>
                    <td className="px-4 py-2 text-right tabular">{fmtNum(r.holiday_hours || 0)}</td>
                    <td className="px-4 py-2 text-right tabular font-semibold text-[#C86235]" data-testid={`ot-holiday-${r.employee_name}`}>{fmtEuro(r.holiday_amount || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-[#3A3833] text-[10px] uppercase tracking-wider text-white/80">
                <th colSpan={14} />
                <th colSpan={2} className="px-3 py-1.5 text-center bg-[#6E7A63] text-white" data-testid="payroll-payment-method-header">Τρόπος πληρωμής</th>
                <th colSpan={editable ? 2 : 1} />
              </tr>
              <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
                <th className="px-3 py-3">#</th>
                <th className="px-3 py-3">Υπάλληλος</th>
                <th className="px-3 py-3">Τμήμα</th>
                <th className="px-3 py-3">Ρόλος / Θέση</th>
                <th className="px-3 py-3 text-right">% εργασίας</th>
                <th className="px-3 py-3 text-right">Υπερωρίες €</th>
                <th className="px-3 py-3 text-right">Ώρες Υπ.</th>
                <th className="px-3 py-3 text-right">Τιμή Υπ. €/ώρα</th>
                <th className="px-3 py-3 text-right">Αργίες €</th>
                <th className="px-3 py-3 text-right">Μηνιαίες</th>
                <th className="px-3 py-3 text-right">Συνολικές Αποδοχές</th>
                <th className="px-3 py-3 text-right">ΦΜΥ</th>
                <th className="px-3 py-3 text-right">ΕΦΚΑ</th>
                <th className="px-3 py-3 text-right bg-[#2E6F7A] text-white" data-testid="payroll-total-cost-header">Συνολικό Κόστος</th>
                <th className="px-3 py-3 text-right bg-[#6E7A63] text-white">Τράπεζα</th>
                <th className="px-3 py-3 text-right bg-[#6E7A63] text-white">Μετρητά</th>
                <th className="px-3 py-3 text-center">Απόδ.</th>
                {editable && <th className="px-3 py-3" />}
              </tr>
            </thead>
            <tbody>
              {working.map(({ it, i }) => (
                <tr key={i} data-testid={`line-item-${it.code}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                  <td className="px-3 py-2 font-mono text-[#6F6A60]">{it.code}</td>
                  <td className="px-3 py-2 font-medium text-[#45423C]">{it.name}</td>
                  <td className="px-3 py-2 text-[#6F6A60]">{it.department || "—"}</td>
                  <td className="px-3 py-2 text-[#6F6A60]">{it.position || "—"}</td>
                  <td className="px-3 py-2 text-right tabular">
                    {editable ? <input className={ci} onFocus={selOnFocus} type="number" step="5" value={Math.round((it.work_pct || 0) * 100)} onChange={(e) => upd(i, "work_pct", (Number(e.target.value) || 0) / 100)} /> : `${Math.round((it.work_pct || 0) * 100)}%`}
                  </td>
                  <td className="px-3 py-2 text-right tabular">
                    {editable ? <input className={ci} onFocus={selOnFocus} type="number" value={it.overtime_amount} onChange={(e) => upd(i, "overtime_amount", e.target.value)} /> : fmtEuro(it.overtime_amount)}
                  </td>
                  <td className="px-3 py-2 text-right tabular text-[#6F6A60]">
                    {editable ? <input className={ci} onFocus={selOnFocus} type="number" value={it.overtime_hours} onChange={(e) => upd(i, "overtime_hours", e.target.value)} /> : it.overtime_hours}
                  </td>
                  <td className="px-3 py-2 text-right tabular" data-testid={`ot-rate-cell-${it.code}`}>
                    {editable
                      ? <input data-testid={`ot-rate-${it.code}`} className={ci} onFocus={selOnFocus} type="number" step="0.5" value={it.overtime_rate || 0}
                          onChange={(e) => upd(i, "overtime_rate", e.target.value)} />
                      : (it.overtime_rate ? fmtEuro(it.overtime_rate) : "—")}
                  </td>
                  <td className="px-3 py-2 text-right tabular" data-testid={`holiday-cell-${it.code}`}>
                    {editable ? <input className={ci} onFocus={selOnFocus} type="number" value={it.holiday_amount || 0} onChange={(e) => upd(i, "holiday_amount", e.target.value)} /> : fmtEuro(it.holiday_amount || 0)}
                  </td>
                  <td className="px-3 py-2 text-right tabular">
                    {editable ? <input className={ci} onFocus={selOnFocus} type="number" value={it.monthly_base} onChange={(e) => upd(i, "monthly_base", e.target.value)} /> : fmtEuro(it.monthly_base)}
                  </td>
                  <td className="px-3 py-2 text-right tabular font-semibold text-[#45423C]">{fmtEuro(it.total_earnings)}</td>
                  <td className="px-3 py-2 text-right tabular" data-testid={`tax-cell-${it.code}`}>
                    {editable
                      ? <input data-testid={`tax-${it.code}`} className={ci} onFocus={selOnFocus} type="number" value={it.tax || 0} onChange={(e) => upd(i, "tax", e.target.value)} />
                      : fmtEuro(it.tax || 0)}
                  </td>
                  <td className="px-3 py-2 text-right tabular" data-testid={`efka-cell-${it.code}`}>
                    {editable
                      ? <input data-testid={`efka-${it.code}`} className={ci} onFocus={selOnFocus} type="number" value={it.efka || 0} onChange={(e) => upd(i, "efka", e.target.value)} />
                      : fmtEuro(it.efka || 0)}
                  </td>
                  <td className="px-3 py-2 text-right tabular font-semibold text-[#1f5560] bg-[#2E6F7A]/5" data-testid={`cost-cell-${it.code}`}>{fmtEuro((it.total_earnings || 0) + (Number(it.efka) || 0))}</td>
                  <td className="px-3 py-2 text-right tabular bg-[#6E7A63]/5">
                    {editable ? <input className={ci} onFocus={selOnFocus} type="number" value={it.bank_deposit} onChange={(e) => upd(i, "bank_deposit", e.target.value)} /> : fmtEuro(it.bank_deposit)}
                  </td>
                  <td className="px-3 py-2 text-right tabular text-[#4A5A3E] font-medium bg-[#6E7A63]/5">{fmtEuro(it.remaining)}</td>
                  <td className="px-3 py-2 text-center">
                    <button data-testid={`payslip-btn-${it.code}`} onClick={() => setPayslip(it)} className="p-1.5 rounded-lg hover:bg-[#F1EDE6] text-[#C86235]">
                      <FileText className="h-4 w-4" />
                    </button>
                  </td>
                  {editable && (
                    <td className="px-3 py-2 text-right">
                      <button data-testid={`remove-line-${it.code}`} title="Αφαίρεση από τη μισθοδοσία"
                        onClick={() => removeItem(i)}
                        className="p-1.5 rounded-lg hover:bg-[#FEF2F2] text-[#991B1B]">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[#45423C] bg-[#F1EDE6] font-semibold text-[#45423C]" data-testid="payroll-totals">
                <td className="px-3 py-3" colSpan={4}>ΣΥΝΟΛΟ{dept !== "all" ? ` — ${dept}` : ""} · {working.length} υπάλληλοι</td>
                <td className="px-3 py-3 text-right tabular">{Math.round(totalPct)}%</td>
                <td className="px-3 py-3 text-right tabular">{fmtEuro(totalOt)}</td>
                <td className="px-3 py-3 text-right tabular">{fmtNum(totalOtHours)}</td>
                <td className="px-3 py-3" />
                <td className="px-3 py-3 text-right tabular" data-testid="payroll-total-holiday">{fmtEuro(totalHoliday)}</td>
                <td className="px-3 py-3 text-right tabular">{fmtEuro(totalBase)}</td>
                <td className="px-3 py-3 text-right tabular">{fmtEuro(totalGross)}</td>
                <td className="px-3 py-3 text-right tabular" data-testid="payroll-total-tax">{fmtEuro(totalTax)}</td>
                <td className="px-3 py-3 text-right tabular" data-testid="payroll-total-efka">{fmtEuro(totalEfka)}</td>
                <td className="px-3 py-3 text-right tabular text-[#1f5560] bg-[#2E6F7A]/10" data-testid="payroll-total-cost">{fmtEuro(totalGross + totalEfka)}</td>
                <td className="px-3 py-3 text-right tabular bg-[#6E7A63]/10">{fmtEuro(totalBank)}</td>
                <td className="px-3 py-3 text-right tabular bg-[#6E7A63]/10">{fmtEuro(totalGross - totalBank)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
        {editable && (
          <div className="px-4 py-3 border-t border-[#E3DED4] bg-[#F7F5F1] flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#6F6A60]">Προσθήκη υπαλλήλου</span>
            <select data-testid="add-line-select" defaultValue="" onChange={(e) => { addEmployee(e.target.value); e.target.value = ""; }}
              className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]">
              <option value="">— επιλέξτε από τον κατάλογο —</option>
              {available.map((e) => (
                <option key={e.id} value={e.name}>{e.name}{e.department ? ` · ${e.department}` : ""}</option>
              ))}
            </select>
            <span className="text-xs text-[#8A8577]">
              {available.length} διαθέσιμοι · οι αλλαγές αποθηκεύονται με το κουμπί «Αποθήκευση»
            </span>
          </div>
        )}
      </div>

      {idle.length > 0 && (
        <div className="rounded-2xl bg-[#FAF9F6] border border-dashed border-[#E3DED4] overflow-hidden" data-testid="payroll-idle-table">
          <div className="px-5 py-4 border-b border-[#E3DED4]">
            <h3 className="font-medium text-[#8A8577]">Δεν έχουν ξεκινήσει / χωρίς αμοιβή — {idle.length} υπάλληλοι</h3>
            <p className="text-xs text-[#B9AE9A] mt-0.5">
              Είναι στον κατάλογο της εταιρείας αλλά δεν εργάστηκαν αυτόν τον μήνα, οπότε δεν πληρώνονται και δεν μπαίνουν στα σύνολα.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap text-[#B9AE9A]">
              <thead>
                <tr className="bg-[#F1EDE6] text-left text-[10px] uppercase tracking-wider text-[#B9AE9A]">
                  <th className="px-3 py-2.5">#</th>
                  <th className="px-3 py-2.5">Υπάλληλος</th>
                  <th className="px-3 py-2.5">Τμήμα</th>
                  <th className="px-3 py-2.5">Ρόλος / Θέση</th>
                  <th className="px-3 py-2.5 text-right">Μηνιαίες</th>
                  <th className="px-3 py-2.5 text-right">% εργασίας</th>
                  <th className="px-3 py-2.5 text-right">Σύνολο</th>
                  {editable && <th className="px-3 py-2.5" />}
                </tr>
              </thead>
              <tbody>
                {idle.map(({ it, i }) => (
                  <tr key={i} data-testid={`idle-item-${it.code}`} className="border-t border-[#E3DED4]">
                    <td className="px-3 py-2 font-mono">{it.code}</td>
                    <td className="px-3 py-2 font-medium">{it.name}</td>
                    <td className="px-3 py-2">{it.department || "—"}</td>
                    <td className="px-3 py-2">{it.position || "—"}</td>
                    <td className="px-3 py-2 text-right tabular">{fmtEuro(it.monthly_base)}</td>
                    <td className="px-3 py-2 text-right tabular">
                      {editable
                        ? <input data-testid={`idle-pct-${it.code}`} className={ci} onFocus={selOnFocus} type="number" step="5"
                            value={Math.round((it.work_pct || 0) * 100)}
                            onChange={(e) => upd(i, "work_pct", (Number(e.target.value) || 0) / 100)} />
                        : `${Math.round((it.work_pct || 0) * 100)}%`}
                    </td>
                    <td className="px-3 py-2 text-right tabular">{fmtEuro(it.total_earnings)}</td>
                    {editable && (
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button data-testid={`restore-idle-${it.code}`} title="Επαναφορά στην ενεργή μισθοδοσία (100% + μισθός από την καρτέλα)"
                            onClick={() => restoreItem(i)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-[#E3DED4] bg-white px-2.5 py-1 text-xs font-semibold text-[#6E7A63] hover:border-[#6E7A63] hover:bg-[#6E7A63]/5 transition-colors">
                            <RotateCcw className="h-3.5 w-3.5" /> Επαναφορά
                          </button>
                          <button data-testid={`remove-idle-${it.code}`} title="Αφαίρεση από τη μισθοδοσία"
                            onClick={() => removeItem(i)} className="p-1.5 rounded-lg hover:bg-[#FEF2F2] text-[#991B1B]/70">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {payslip && <PayslipModal item={payslip} run={run} onClose={() => setPayslip(null)} />}
      {showBulk && <BulkPayslips run={run} items={working.map(({ it }) => it)} />}
    </div>
  );
}

export default function Payroll() {
  const [runId, setRunId] = useState(null);
  return (
    <div className="space-y-6" data-wide data-testid="payroll-page">
      <div className="flex justify-end"><ImportButton only={["payroll"]} label="Ανέβασμα μισθοδοσίας / Φόρου & ΕΦΚΑ" /></div>
      {!runId && (
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">2026</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Μισθοδοσίες</h1>
          <p className="text-[#6F6A60] text-sm mt-1">Μηνιαίοι μισθοί, δώρα και άδειες — αυτόματος υπολογισμός σε Ευρώ. Επιλέξτε μισθοδοσία για προβολή ή επεξεργασία.</p>
        </div>
      )}
      {runId ? <RunDetail runId={runId} onBack={() => setRunId(null)} /> : <RunList onOpen={setRunId} />}
    </div>
  );
}
